import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/apiError";
import { Toaster, toast } from "sonner";

const AdminLogin = () => {
  const { login, login2FA, user } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 2FA challenge state
  const [challengeToken, setChallengeToken] = useState(null);
  const [twoFACode, setTwoFACode] = useState("");

  // Already authenticated → redirect
  React.useEffect(() => {
    if (user && typeof user === "object") {
      const dest = location.state?.from || "/admin";
      navigate(dest, { replace: true });
    }
  }, [user, location.state, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email.trim(), password);
      if (result?.requires_2fa) {
        setChallengeToken(result.challenge_token);
        return; // wait for 2FA submit
      }
      const dest = location.state?.from || "/admin";
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(
        err,
        lang === "fr" ? "Connexion impossible" : "Login failed",
      );
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login2FA(challengeToken, twoFACode.trim());
      const dest = location.state?.from || "/admin";
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(
        err,
        lang === "fr" ? "Code invalide" : "Invalid code",
      );
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resetTo2FAStep = () => {
    setChallengeToken(null);
    setTwoFACode("");
    setError("");
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#050505] text-[#F2EBE5] flex items-center justify-center px-6 py-20"
    >
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#0a0a0a",
            border: "1px solid rgba(197,160,89,0.3)",
            color: "#F2EBE5",
          },
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="w-full max-w-md"
      >
        <Link
          to="/"
          className="kw-btn-ghost mb-10 inline-flex"
          data-testid="admin-login-back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.25} />
          {lang === "fr" ? "Retour au site" : "Back to site"}
        </Link>

        <p className="overline kw-accent-line">
          {lang === "fr" ? "Espace administrateur" : "Admin area"}
        </p>
        <h1 className="font-serif-display text-5xl md:text-6xl mt-6 leading-[1]">
          {challengeToken ? (
            <em className="text-[#C5A059]">
              {lang === "fr" ? "Vérification" : "Verification"}
            </em>
          ) : (
            <em className="text-[#C5A059]">
              {lang === "fr" ? "Connexion" : "Sign in"}
            </em>
          )}
        </h1>
        <p className="mt-4 text-[#A19E9C] text-sm">
          {challengeToken
            ? lang === "fr"
              ? "Saisissez le code à 6 chiffres de Microsoft Authenticator (ou un code de secours)."
              : "Enter the 6-digit code from Microsoft Authenticator (or a recovery code)."
            : lang === "fr"
              ? "Réservé à l'équipe Kiwami Studios."
              : "Restricted to Kiwami Studios team."}
        </p>

        {challengeToken ? (
          <form
            onSubmit={handle2FASubmit}
            className="mt-12 space-y-8"
            data-testid="admin-2fa-form"
          >
            <div>
              <label
                className="kw-label flex items-center gap-2 mb-3"
                htmlFor="twofa_code"
              >
                <Lock className="w-3 h-3 text-[#C5A059]" strokeWidth={1.5} />
                {lang === "fr" ? "Code 2FA" : "2FA code"}
              </label>
              <input
                id="twofa_code"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                required
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value)}
                data-testid="admin-2fa-code"
                className="kw-input font-mono tracking-widest"
                placeholder="123456"
                autoFocus
              />
              <p className="mt-2 text-[10px] text-[#A19E9C] italic">
                {lang === "fr"
                  ? "6 chiffres, ou un code de secours au format xxxx-xxxx-xxxx."
                  : "6 digits, or a recovery code (xxxx-xxxx-xxxx)."}
              </p>
            </div>

            {error && (
              <p
                data-testid="admin-2fa-error"
                className="text-sm text-red-400 border-l-2 border-red-400/40 pl-3"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-2fa-submit"
              className="kw-btn-primary w-full justify-center disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  {lang === "fr" ? "Vérification…" : "Verifying…"}
                </>
              ) : (
                <>{lang === "fr" ? "Vérifier" : "Verify"}</>
              )}
            </button>
            <button
              type="button"
              onClick={resetTo2FAStep}
              data-testid="admin-2fa-cancel"
              className="kw-btn-ghost"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
              {lang === "fr" ? "Retour" : "Back"}
            </button>
          </form>
        ) : (
        <form onSubmit={handleSubmit} className="mt-12 space-y-8" data-testid="admin-login-form">
          <div>
            <label className="kw-label flex items-center gap-2 mb-3" htmlFor="email">
              <Mail className="w-3 h-3 text-[#C5A059]" strokeWidth={1.5} />
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="kw-input"
              placeholder="contact@kiwamistudios.com"
            />
          </div>

          <div>
            <label className="kw-label flex items-center gap-2 mb-3" htmlFor="password">
              <Lock className="w-3 h-3 text-[#C5A059]" strokeWidth={1.5} />
              {lang === "fr" ? "Mot de passe" : "Password"}
            </label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="kw-input"
              placeholder="•••••••••"
            />
          </div>

          {error && (
            <p
              data-testid="admin-login-error"
              className="text-sm text-red-400 border-l-2 border-red-400/40 pl-3"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="kw-btn-primary w-full justify-center disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                {lang === "fr" ? "Connexion…" : "Signing in…"}
              </>
            ) : (
              <>{lang === "fr" ? "Se connecter" : "Sign in"}</>
            )}
          </button>

          <div className="text-center pt-2">
            <Link
              to="/admin/forgot-password"
              data-testid="admin-forgot-password-link"
              className="text-[11px] uppercase tracking-[0.25em] text-[#A19E9C] hover:text-[#C5A059] transition-colors"
            >
              {lang === "fr" ? "Mot de passe oublié ?" : "Forgot password?"}
            </Link>
          </div>
        </form>
        )}
      </motion.div>
    </div>
  );
};

export default AdminLogin;
